import type {
    SubscriptionChangeApplyResult,
    SubscriptionChangePreview,
    SubscriptionChangeRequest,
} from '@fruitful/types';
import { useQueryClient } from '@tanstack/react-query';

import type { MembershipDetails } from '../../entities/entityTypes';
import { useRefetchMembershipDetailsMutationOptions } from '../../entities/membership/hooks/useRefetchMembershipDetailsMutationOptions';
import { AllMembershipsQueryKey } from '../../entities/membership/MembershipEntityProvider';
import { requestBuilder } from '../../http/RequestBuilder';

const usePreviewSubscriptionChange = requestBuilder.mutation<
    SubscriptionChangePreview,
    SubscriptionChangeRequest,
    { id: string }
>({
    endpoint: ({ id }) =>
        `/api/admin/memberships/${id}/subscription-change/preview`,
    method: 'POST',
});

const useApplySubscriptionChange = requestBuilder.mutation<
    SubscriptionChangeApplyResult,
    SubscriptionChangeRequest,
    { id: string }
>({
    endpoint: ({ id }) => `/api/admin/memberships/${id}/subscription-change`,
    method: 'POST',
});

export const useChangeSubscriptionAndRefetch = (
    membership: MembershipDetails | null,
) => {
    const queryClient = useQueryClient();
    const refetchMembershipMutationOptions =
        useRefetchMembershipDetailsMutationOptions(membership);
    const { mutateAsync: previewMutateAsync } = usePreviewSubscriptionChange();
    const { mutateAsync: applyMutateAsync } = useApplySubscriptionChange({
        ...refetchMembershipMutationOptions,
        onSuccess: () => {
            refetchMembershipMutationOptions.onSuccess();
            void queryClient.invalidateQueries({
                queryKey: [AllMembershipsQueryKey],
            });
        },
    });

    const previewSubscriptionChangeAsync = async (
        membershipId: string,
        body: SubscriptionChangeRequest,
    ) => {
        return await previewMutateAsync({
            urlParams: {
                id: membershipId,
            },
            body,
        });
    };

    const applySubscriptionChangeAsync = async (
        membershipId: string,
        body: SubscriptionChangeRequest,
    ) => {
        return await applyMutateAsync({
            urlParams: {
                id: membershipId,
            },
            body,
        });
    };

    return {
        previewSubscriptionChangeAsync,
        applySubscriptionChangeAsync,
    };
};
